import React from 'react';
import { useTranslation } from 'react-i18next';
import { ITask } from '../../types/task';
import './TasksList.css';

interface TasksListProps {
    tasks: ITask[];
    loading: boolean;
    onToggleStatus: (taskId: string) => void;
    onEdit?: (task: ITask) => void;
}

const TasksList: React.FC<TasksListProps> = ({ tasks, loading, onToggleStatus, onEdit }) => {
    const { t } = useTranslation();

    const formatDate = (dateStr?: string): string => {
        if (!dateStr) return t('tasks.never');
        try {
            return new Date(dateStr).toLocaleString();
        } catch {
            return dateStr;
        }
    };

    if (loading) {
        return (
            <div className="tasks-list-loading">
                <span className="material-icons rotating">sync</span>
                <span>{t('loading.tasks')}</span>
            </div>
        );
    }

    return (
        <div className="tasks-list-container">
            <table className="tasks-table">
                <thead>
                    <tr>
                        <th>{t('tasks.name')}</th>
                        <th>{t('tasks.status')}</th>
                        <th>{t('tasks.group')}</th>
                        <th>{t('tasks.pipelineOrder')}</th>
                        <th>{t('tasks.provider')}</th>
                        <th>{t('tasks.lastRun')}</th>
                        <th>{t('tasks.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {tasks.map((task) => (
                        <tr key={task._id} className={task.is_running ? 'running-row' : ''}>
                            <td>
                                <div className="task-name-cell">
                                    <span className="task-name-text">{task.name}</span>
                                    {task.description && (
                                        <span className="task-description-text">{task.description}</span>
                                    )}
                                </div>
                            </td>
                            <td>
                                <div className="task-status-cell">
                                    {task.is_running && (
                                        <span className="running-indicator">
                                            <span className="material-icons rotating">sync</span>
                                        </span>
                                    )}
                                    <span className={`status-badge ${task.status?.toLowerCase() || 'unknown'}`}>
                                        {task.status || 'Unknown'}
                                    </span>
                                </div>
                            </td>
                            <td>
                                {task.group ? (
                                    <span className="group-badge">{task.group}</span>
                                ) : (
                                    <span className="no-group">-</span>
                                )}
                            </td>
                            <td className="center-cell">{task.pipeline_order}</td>
                            <td>{task.provider || '-'}</td>
                            <td className="last-run-cell">{formatDate(task.last_run)}</td>
                            <td>
                                <div className="actions-cell">
                                    <button
                                        className={`action-btn toggle-btn ${task.status === 'ACTIVE' ? 'deactivate' : 'activate'}`}
                                        onClick={() => onToggleStatus(task._id)}
                                        title={task.status === 'ACTIVE' ? t('tasks.deactivate') : t('tasks.activate')}
                                    >
                                        <span className="material-icons">
                                            {task.status === 'ACTIVE' ? 'pause_circle' : 'play_circle'}
                                        </span>
                                    </button>
                                    {onEdit && (
                                        <button
                                            className="action-btn edit-btn"
                                            onClick={() => onEdit(task)}
                                            title={t('tasks.edit')}
                                        >
                                            <span className="material-icons">edit</span>
                                        </button>
                                    )}
                                    {task.logs_url && (
                                        <a
                                            href={task.logs_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="action-btn logs-btn"
                                            title={t('tasks.viewLogs')}
                                        >
                                            <span className="material-icons">article</span>
                                        </a>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default TasksList;
